/**
 * Admin → User Details drawer.
 *
 * Round 39 — gives the operator one click to see EVERYTHING about a user
 * (chart inputs that live on multi-profile rows, every question, every
 * payment, every consultation, plus aggregate totals) without having to
 * SSH into the production pod and run Mongo queries by hand.
 */
import { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, readErr } from '@/lib/api';
import { toast } from '@/lib/toast';
import { Loader2 } from 'lucide-react';

export default function UserDetailsDrawer({ user, open, onOpenChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!open || !user?.id) return;
        let alive = true;
        setLoading(true);
        setErr(null);
        setData(null);
        api.get(`/admin/users/${user.id}`)
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) { setErr(readErr(e, 'Failed to load')); toast.error(e); } })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, user?.id]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-surface border-marigold/15 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto"
                data-testid="user-details-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-parchment">User details</DialogTitle>
                </DialogHeader>

                {loading && (
                    <div className="flex items-center gap-2 text-slate-400 py-10 justify-center">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}

                {err && !loading && (
                    <div className="py-6 text-rose-400 text-sm" data-testid="user-details-error">{err}</div>
                )}

                {data && !loading && (
                    <div className="space-y-6 text-sm">
                        <Section title="Account">
                            <KV k="ID"             v={data.user.id} mono />
                            <KV k="Email"          v={data.user.email} />
                            <KV k="Name"           v={data.user.name} />
                            <KV k="Wallet"         v={`₹${Number(data.user.wallet_balance_inr ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                            <KV k="Joined"         v={fmtDate(data.user.created_at)} />
                            <KV k="Admin"          v={data.user.is_admin ? 'Yes' : 'No'} />
                            <KV k="Banned"         v={data.user.banned ? 'Yes' : 'No'} />
                            <KV k="Email verified" v={data.user.email_verified ? 'Yes' : 'No'} />
                            <KV k="Referral code"  v={data.user.referral_code} mono />
                            <KV k="Display TZ"     v={data.user.display_timezone} />
                            <ResidenceCountryRow
                                userId={data.user.id}
                                value={data.user.residence_country}
                                onSaved={(rc) => setData((d) => d
                                    ? { ...d, user: { ...d.user, residence_country: rc } }
                                    : d)}
                            />
                        </Section>

                        <Section title={`Birth profiles (${data.profiles?.length || 0})`} testid="user-details-profiles">
                            {(!data.profiles || data.profiles.length === 0) && (
                                <div className="text-slate-500 text-xs italic px-2">No profiles on file.</div>
                            )}
                            {data.profiles?.map((p) => (
                                <div key={p.id} className="border border-marigold/10 rounded-sm p-3 mb-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-parchment font-medium">{p.name}</span>
                                        <Badge className="bg-elevated text-slate-400 border-marigold/15 text-[10px]">{p.relation || 'self'}</Badge>
                                        {p.is_default && <Badge className="bg-marigold/15 text-marigold border-marigold/30 text-[10px]">default</Badge>}
                                        <Badge className="bg-elevated text-slate-400 border-marigold/15 text-[10px]">{p.birth_time_confidence || 'not_set'}</Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-1 gap-x-3 pl-1">
                                        <KV k="Date"  v={p.birth_details?.date} />
                                        <KV k="Time"  v={p.birth_details?.time} />
                                        <KV k="Place" v={p.birth_details?.place} />
                                        <KV k="Lat"   v={p.birth_details?.lat} />
                                        <KV k="Lon"   v={p.birth_details?.lon} />
                                        <KV k="TZ"    v={p.birth_details?.birth_timezone || p.birth_details?.tz} />
                                    </div>
                                </div>
                            ))}
                        </Section>

                        <Section title={`Questions — ${data.totals?.questions ?? 0} total (showing latest ${data.questions?.length || 0})`} testid="user-details-questions">
                            {(!data.questions || data.questions.length === 0) && (
                                <div className="text-slate-500 text-xs italic px-2">No questions asked yet.</div>
                            )}
                            {data.questions?.map((q) => (
                                <div key={q.id} className="border-b border-marigold/5 py-2 text-xs">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-parchment truncate">{q.text}</span>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Badge className="bg-elevated text-slate-400 border-marigold/15 text-[10px]">{q.mode || 'auto'}</Badge>
                                            {q.horary_number && <Badge className="bg-elevated text-slate-400 border-marigold/15 text-[10px]">#{q.horary_number}</Badge>}
                                            <Badge className="bg-elevated text-marigold border-marigold/30 text-[10px]">{q.verdict || '?'}</Badge>
                                        </div>
                                    </div>
                                    <div className="text-slate-500 mt-0.5">
                                        {fmtDate(q.created_at)} · {q.category || '—'}{q.profile_name ? ` · for ${q.profile_name}` : ''}
                                    </div>
                                </div>
                            ))}
                        </Section>

                        <Section title={`Payments — ₹${(data.totals?.paid_inr ?? 0).toLocaleString('en-IN')} lifetime (showing latest ${data.payments?.length || 0})`} testid="user-details-payments">
                            {(!data.payments || data.payments.length === 0) && (
                                <div className="text-slate-500 text-xs italic px-2">No payments on file.</div>
                            )}
                            {data.payments?.map((p) => (
                                <div key={p.id || p.razorpay_payment_id} className="border-b border-marigold/5 py-2 text-xs flex justify-between">
                                    <div>
                                        <div className="text-parchment font-mono">{p.razorpay_payment_id || p.id}</div>
                                        <div className="text-slate-500">{fmtDate(p.created_at)} · {p.status}</div>
                                    </div>
                                    <div className="text-marigold">₹{Number(p.amount_inr || p.amount || 0).toLocaleString('en-IN')}</div>
                                </div>
                            ))}
                        </Section>

                        <Section title={`Consultations (${data.consultations?.length || 0})`} testid="user-details-consultations">
                            {(!data.consultations || data.consultations.length === 0) && (
                                <div className="text-slate-500 text-xs italic px-2">No consultations booked.</div>
                            )}
                            {data.consultations?.map((c) => (
                                <div key={c.id} className="border-b border-marigold/5 py-2 text-xs">
                                    <div className="text-parchment">{c.expert_name || c.expert_id} · {c.duration_minutes || c.duration}m</div>
                                    <div className="text-slate-500">{fmtDate(c.created_at)} · {c.status}</div>
                                </div>
                            ))}
                        </Section>

                        <Section
                            title={`Chat sessions with Guruji (showing latest ${data.chat_sessions?.length || 0})`}
                            testid="user-details-chats"
                        >
                            {(!data.chat_sessions || data.chat_sessions.length === 0) && (
                                <div className="text-slate-500 text-xs italic px-2">No chat sessions yet.</div>
                            )}
                            {data.chat_sessions?.map((s) => (
                                <ChatSessionThread key={s.id} session={s} />
                            ))}
                        </Section>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="user-details-close"
                        className="bg-transparent border-marigold/20 text-slate-300 rounded-sm">
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Section({ title, children, testid }) {
    return (
        <div data-testid={testid}>
            <div className="overline text-marigold mb-2">{title}</div>
            <div className="bg-obsidian/40 border border-marigold/10 rounded-sm p-3">{children}</div>
        </div>
    );
}

function KV({ k, v, mono }) {
    const valueClass = `text-parchment ${mono ? 'font-mono text-xs' : ''}`;
    const shown = (v === null || v === undefined || v === '') ? '—' : String(v);
    return (
        <div className="flex justify-between gap-3 py-0.5">
            <span className="text-slate-500 text-xs uppercase tracking-wider">{k}</span>
            <span className={valueClass}>{shown}</span>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    } catch {
        return iso;
    }
}

/**
 * Renders one chat session as a collapsible thread of role-tagged
 * bubbles. Handles all message kinds the chat engine emits:
 *   • plain text         → content.text
 *   • greeting           → content.text
 *   • event reading      → content.bubbles[] (Guruji's 4-bubble structure)
 *   • followup           → content.reply
 *   • horary picker      → content.text + meta
 * Long sessions stay collapsed by default; click "Expand" to read.
 */
function ChatSessionThread({ session }) {
    const [open, setOpen] = useState(false);
    const messages = session.messages || [];
    const headline = session.first_question
        || (messages[0]?.content?.text)
        || '(no question recorded)';
    return (
        <div className="border border-marigold/10 rounded-sm mb-3 bg-obsidian/30" data-testid={`chat-session-${session.id}`}>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full text-left px-3 py-2 flex items-start justify-between gap-3"
                data-testid={`chat-session-toggle-${session.id}`}
            >
                <div className="min-w-0">
                    <div className="text-parchment text-xs truncate" title={headline}>{headline}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">
                        {fmtDate(session.started_at)} · {session.status}
                        {session.horary_number ? ` · #${session.horary_number}` : ''}
                        {session.tick_count ? ` · ${session.tick_count} min` : ''}
                        {session.total_deducted_inr ? ` · ₹${session.total_deducted_inr.toFixed(2)} spent` : ''}
                        {` · ${messages.length} messages`}
                    </div>
                </div>
                <span className="text-marigold text-[10px] uppercase tracking-wider shrink-0">
                    {open ? 'Collapse' : 'Expand'}
                </span>
            </button>
            {open && (
                <div className="px-3 pb-3 pt-1 space-y-2 border-t border-marigold/5">
                    {messages.length === 0 && (
                        <div className="text-slate-500 text-xs italic">No messages stored.</div>
                    )}
                    {messages.map((m) => <ChatBubbleRow key={m.id} m={m} />)}
                </div>
            )}
        </div>
    );
}

function ChatBubbleRow({ m }) {
    const isUser = m.role === 'user';
    const c = m.content || {};
    const bubbles = Array.isArray(c.bubbles) ? c.bubbles : null;
    const chips = Array.isArray(c.chips) ? c.chips : null;
    const text = c.text || c.reply || (c.kind === 'event' ? '' : '');
    const roleLabel = isUser ? 'User' : (m.delivery === 'guruji_event' ? 'Guruji (reading)' : 'Guruji');
    return (
        <div className={`text-xs ${isUser ? 'pl-6' : 'pr-6'}`}>
            <div className={`text-[10px] mb-1 ${isUser ? 'text-slate-400 text-right' : 'text-marigold'}`}>
                {roleLabel} · {fmtDate(m.created_at)}
            </div>
            <div className={`rounded-sm p-2 whitespace-pre-wrap ${isUser ? 'bg-marigold/10 text-parchment' : 'bg-elevated/60 text-slate-200 border border-marigold/10'}`}>
                {text && <div>{text}</div>}
                {bubbles && bubbles.map((b, i) => (
                    <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-marigold/10' : ''}>
                        {b}
                    </div>
                ))}
                {chips && chips.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                        {chips.map((chip, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full bg-marigold/15 text-marigold text-[10px]">{chip}</span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


// Round 40o.7 — inline editor for `residence_country`. Indispensable
// when a user (e.g. Indian-passport-holder signing up first time on
// UK Wi-Fi) gets silently locked into the wrong currency because the
// backend's IP-detection ladder fell through to its `or 'IN'` default.
// Compact list — the long tail can still be typed via the text input.
const COUNTRY_PRESETS = [
    { code: 'IN', label: '🇮🇳 India · INR' },
    { code: 'US', label: '🇺🇸 United States · USD' },
    { code: 'GB', label: '🇬🇧 United Kingdom · USD' },
    { code: 'CA', label: '🇨🇦 Canada · USD' },
    { code: 'AU', label: '🇦🇺 Australia · USD' },
    { code: 'AE', label: '🇦🇪 UAE · USD' },
    { code: 'SG', label: '🇸🇬 Singapore · USD' },
    { code: 'DE', label: '🇩🇪 Germany · USD' },
    { code: 'FR', label: '🇫🇷 France · USD' },
];

function ResidenceCountryRow({ userId, value, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState((value || '').toUpperCase());
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const rc = (draft || '').trim().toUpperCase();
        if (rc.length !== 2 || !/^[A-Z]{2}$/.test(rc)) {
            toast.error('Country must be a 2-letter ISO code (e.g. IN, US, GB)');
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/admin/users/${userId}`, { residence_country: rc });
            onSaved && onSaved(rc);
            toast.success(`Country updated to ${rc}`);
            setEditing(false);
        } catch (e) {
            toast.error(e);
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <div className="flex items-center gap-2" data-testid="user-detail-country">
                <KV k="Residence country" v={value || '—'} />
                <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[10px] border-marigold/30 text-marigold hover:bg-marigold/10"
                    onClick={() => { setDraft((value || '').toUpperCase()); setEditing(true); }}
                    data-testid="user-detail-country-edit-btn"
                >
                    Edit
                </Button>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="user-detail-country-editor">
            <span className="text-slate-500 min-w-[140px]">Residence country</span>
            <select
                value={COUNTRY_PRESETS.some(p => p.code === draft) ? draft : ''}
                onChange={(e) => setDraft(e.target.value)}
                className="bg-elevated border border-marigold/20 rounded-sm px-2 py-1 text-xs text-parchment"
                data-testid="user-detail-country-select"
            >
                <option value="" disabled>Pick preset…</option>
                {COUNTRY_PRESETS.map((p) => (
                    <option key={p.code} value={p.code}>{p.label}</option>
                ))}
            </select>
            <input
                value={draft}
                onChange={(e) => setDraft(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="ISO-2"
                maxLength={2}
                className="bg-elevated border border-marigold/20 rounded-sm px-2 py-1 text-xs text-parchment w-16 font-mono"
                data-testid="user-detail-country-input"
            />
            <Button
                size="sm"
                className="h-7 bg-marigold/20 text-marigold border border-marigold/40 hover:bg-marigold/30"
                disabled={saving}
                onClick={save}
                data-testid="user-detail-country-save-btn"
            >
                {saving ? <Loader2 className="animate-spin" size={12} /> : 'Save'}
            </Button>
            <Button
                size="sm"
                variant="ghost"
                className="h-7 text-slate-400"
                onClick={() => setEditing(false)}
                data-testid="user-detail-country-cancel-btn"
            >
                Cancel
            </Button>
        </div>
    );
}
